<template>
    <modal :open="showModal" @close="closeModal">
        <template v-if="showModal">
            <div class="bg-primary-500 modal-header">
                <button type="button" @click="closeModal()" class="close" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
                <div class="text-center text-white">
                    <h4 class="modal-title">{{ $t('users.invite_user') }}</h4>
                </div>
            </div>
            <div class="modal-body">
                <p v-html="$t('users.invite_user_details', { lineBreak: '<br/>' })"></p>
                <form class="mt-4 mb-8" @submit.prevent="onSubmit" @keydown="form.errors.forget($event.target.name)">
                    <div class="mb-3" :class="{ 'has-error': form.errors.has('email') }">
                        <label class="mb-1" for="email">{{ $t('auth.email') }}</label>
                        <input class="form-control" id="email" name="email" type="email" :placeholder="$t('sign_up.email_placeholder')" required v-model="form.email" />
                        <p class="help-block" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
                    </div>
                    <div class="pt-2">
                        <button class="btn btn-block btn-primary" :disabled="form.errors.any() || form.busy" type="submit">
                            {{ $t('users.send_invitation') }}
                        </button>
                    </div>
                </form>
                <div v-if="invitationSent" class="mb-8 w-full">
                    <div class="bg-gray-200 border-l-4 border-primary-500 p-4 rounded shadow" role="alert">
                        <button @click="invitationSent = false" type="button" class="close" data-dismiss="alert" aria-label="Close">
                            <span aria-hidden="true">×</span>
                        </button>
                        <p class="mb-0">{{ $t('users.invitation_sent') }}</p>
                    </div>
                </div>
                <div class="mb-2">
                    <h3 class="font-bold text-sm">{{ $t('users.sent_invitations') }}</h3>
                </div>
                <div class="flex flex-col items-center">
                    <div v-if="displayedInvitations.length === 0" class="flex flex-col items-center justify-center w-full">
                        <div class="md:w-1/4 pb-8 w-1/2">
                            <svg-file-searching></svg-file-searching>
                        </div>
                        <div class="md:w-1/2 text-center w-full">
                            <h5>{{ $t('users.no_invitations_sent') }}</h5>
                            <p>{{ $t('users.no_invitations_sent_info') }}</p>
                        </div>
                    </div>
                    <div v-else class="w-full">
                        <div class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{{ $t('users.sent_to') }}</th>
                                        <th>{{ $t('globals.details') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(invitation, index) in displayedInvitations">
                                        <td>{{ index + 1 }}</td>
                                        <td>{{ invitation.email }}</td>
                                        <td v-html="$t('users.author_and_date', {
                                            emphasizedOpen: '<span>',
                                            name: `${invitation.sender.first_name} ${invitation.sender.last_name}`,
                                            emphasizedClose: '</span>',
                                            date: formatDate(invitation.created_at),
                                        })"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <ul v-if="totalPages && totalPages > 1" class="flex justify-center pagination">
                            <li :class="{ 'disabled': currentPage === 1 }">
                                <a @click="currentPage !== 1 && currentPage--" href="javascript:;">&laquo;</a>
                            </li>
                            <li :class="{ 'active': page.active, 'disabled': page.disabled }" v-for="page in displayedPages">
                                <a v-if="page.disabled" href="javascript:;">...</a>
                                <a v-else @click="currentPage = page.label" href="javascript:;">{{ page.label }}</a>
                            </li>
                            <li :class="{ 'disabled': currentPage === totalPages }">
                                <a @click="currentPage !== totalPages && currentPage++" href="javascript:;">&raquo;</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="modal-footer"></div>
        </template>
    </modal>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';
import Modal from '../../../user/partials/modal.vue';
import SvgFileSearching from '../../../svg/file-searching.vue';

export default {
    components: {
        'modal': Modal,
        'svg-file-searching': SvgFileSearching,
    },

    data() {
        return {
            showModal: false,
            invitationSent: false,
            invitations: [],
            currentPage: 1,
            perPage: 5,
            pages: [],
            form: new Form({
                in_current_organization: true,
                email: '',
            }),
        };
    },

    computed: {
        displayedInvitations() {
            let from = (this.currentPage * this.perPage) - this.perPage;
            let to = (this.currentPage * this.perPage);
            return this.invitations.slice(from, to);
        },

        totalPages() {
            return [...this.pages].pop();
        },

        displayedPages() {
            const previousPage = this.currentPage > 1 ? (this.currentPage - 1) : 1;
            const nextPage = this.currentPage < this.totalPages ? (this.currentPage + 1) : this.totalPages;
            const secondPreviousPage = previousPage === 3 ? 2 : null;
            const secondNextPage = nextPage === (this.totalPages - 2) ? (this.totalPages - 1) : null;
            const dotsBefore = previousPage > 3 ? 2 : null;
            const dotsAfter = nextPage < (this.totalPages - 2) ? (this.totalPages - 1) : null;

            let displayedPages = [];
            for (let page = 1; page <= this.totalPages; page++) {
                if ([1, this.totalPages, this.currentPage, previousPage, nextPage, secondPreviousPage, secondNextPage, dotsBefore, dotsAfter].includes(page)) {
                    displayedPages.push({
                        label: page,
                        active: this.currentPage === page,
                        disabled: [dotsBefore, dotsAfter].includes(page)
                    });
                }
            }
            return displayedPages;
        },
    },

    created() {
        Bus.$on('showInviteUserModal', () => {
            this.openModal();
        });
    },

    watch: {
        invitations() {
            this.setPages();
        },
    },

    methods: {
        openModal() {
            axios.get('/api/v1/invitations?sort=-created_at')
                .then(response => this.invitations = response.data)
                .then(() => this.showModal = true)
                .catch(() => {
                    window.toastr.clear();
                    window.toastr.error(this.$t('globals.unexpected_error'));
                });
        },

        closeModal() {
            this.showModal = false;
            this.invitationSent = false;
            this.resetForm();
            this.currentPage = 1;
        },

        setPages() {
            this.pages = [];
            const numberOfPages = Math.ceil(this.invitations.length / this.perPage);
            for (let index = 1; index <= numberOfPages; index++) {
                this.pages.push(index);
            }
        },

        onSubmit() {
            Globals.post('/api/v1/invitations', this.form)
                .then(() => this.invitationSent = true)
                .then(() => axios.get('/api/v1/invitations?sort=-created_at'))
                .then(response => this.invitations = response.data)
                .then(() => this.resetForm())
                .catch(error => {
                    if (error.message !== 'The given data was invalid.') {
                        window.toastr.clear();
                        window.toastr.error(this.$t('globals.unexpected_error'));
                    }
                });
        },

        resetForm() {
            this.form = new Form({
                in_current_organization: true,
                email: '',
            });
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y');
        },
    },
};
</script>
